
<template>
  <button
    :class="classes"
    :disabled="disabled"
  >
    <template v-if="$slots.icon">
      <slot class="icon" name="icon" />
      {{ text }}
    </template>

    <slot
      class="icon"
      v-else-if="$slots.default"
    />

    <template v-else>
      {{ text }}
    </template>
  </button>
</template>

<script>
export default {
  name: 'VNavButton',
  props: {
    text: [String, Number],
    onBrand: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'primary',
      validator(value) {
        return ['primary', 'secondary', 'tertiary'].includes(value);
      },
    },
    size: {
      type: String,
      default: 'lg',
      validator(value) {
        return ['lg', 'sm'].includes(value);
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    }
  },

  computed: {
    classes() {
      const size = `size--${this.size}`;
      const variant = `button--${this.variant}${this.onBrand ? '_onBrand' : ''}`;

      return {
        'button-component': true,
        [variant]: true,
        [size]: true,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.button-component {
  display: flex;
  position: relative;
  height: 100%;
  justify-content: center;
  align-items: center;
  padding: 2px 8px;
  line-height: 150%;
  font-weight: 600;
  text-align: center;
  border: 1px solid transparent;
  border-radius: 4px;
  box-sizing: border-box;
  cursor: pointer;
  transition: background-color 0.3s ease;

  svg {
    margin-right: 8px;
  }
}

.button {
  &--primary {
    background-color: var( --color-interaction-accent-1);
    color: var(--color-interaction-on-brand-accent-1);

    &:hover {
      background-color: var( --color-interaction-accent-2);
    }

    &:active, &:focus {
      border: 2px solid transparent;
      background-color: var( --color-interaction-accent-2);
      box-shadow: inset 0 0 0 2px var(--color-button-primary-lighten);
    }

    &:disabled {
      background:  rgba(22, 22, 22, 0.08);
      color: rgba(22, 22, 22, 0.24);
    }
  }

  &--primary_onBrand {
    background-color: var(--color-interaction-on-brand-accent-1);
    color:  var(--color-interaction-accent-1);

    &:hover {
      background-color: var(--color-interaction-on-brand-accent-2);
      color:  var(--color-interaction-accent-2);
    }

    &:focus {
      border: 2px solid transparent;
      background-color:   var(--color-interaction-on-brand-accent-2);
      box-shadow: inset 0 0 0 2px var(--color-interaction-accent-1);
    }

    &:active {
      border: none;
      background-color: rgba(254, 254, 254, 0.72);
      color:  var(--color-interaction-accent-3);
    }

    &:disabled {
      background: rgba(254, 254, 254, 0.12);
      color: rgba(254, 254, 254, 0.32);
    }
  }

  &--secondary {
    background-color: transparent;
    border: 1px solid  var( --color-interaction-accent-1);
    color:  var( --color-interaction-accent-1);

    &:hover {
      border:1px solid var( --color-interaction-accent-2);
      background-color: rgba(0, 55, 255, 0.12);
      color: var( --color-interaction-accent-2);
    }

    &:focus {
      border: 2px solid var( --color-interaction-accent-2);
      background-color: rgba(0, 55, 255, 0.12);
      color: var( --color-interaction-accent-2);
      box-shadow: inset 0 0 0 2px #fff;
    }

    &:active {
      border: 1px solid var( --color-interaction-accent-3);
      background-color:rgba(0, 55, 255, 0.24);
      color: var( --color-interaction-accent-3);
    }

    &:disabled {
      border: 1px solid rgba(22, 22, 22, 0.24);
      color: rgba(22, 22, 22, 0.24);
    }
  }

  &--secondary_onBrand {
    background-color: transparent;
    border: 1px solid  var( --color-interaction-on-brand-accent-1);
    color:  var( --color-interaction-on-brand-accent-1);

    &:hover {
      background-color: rgba(254, 254, 254, 0.12);
      border: 1px solid  var( --color-interaction-on-brand-accent-1);
      color:  var( --color-interaction-on-brand-accent-1);
    }

    &:focus {
      background-color: rgba(254, 254, 254, 0.12);
      border: 2px solid  var( --color-interaction-on-brand-accent-1);
      color:  var( --color-interaction-on-brand-accent-1);
      box-shadow: inset 0 0 0 2px rgba(22, 22, 22, 0.24);
    }

    &:active {
      background-color:  rgba(254, 254, 254, 0.24);
      border: 1px solid  var( --color-interaction-on-brand-accent-1);
      color:  var( --color-interaction-on-brand-accent-1);
    }

    &:disabled {
      border: 1px solid  rgba(254, 254, 254, 0.32);
      color:  rgba(254, 254, 254, 0.32);

    }
  }

  &--tertiary {
    background-color: transparent;
    color: var( --color-interaction-accent-1);

    &:hover {
      background-color: rgba(0, 55, 255, 0.12);
      color: var( --color-interaction-accent-2);
    }

    &:focus {
      background-color:  rgba(0, 55, 255, 0.12);
      color: var( --color-interaction-accent-2);
      border: 2px solid var(--color-button-primary);
      box-shadow: inset 0 0 0 2px #fff;
    }

    &:active {
      background-color:rgba(0, 55, 255, 0.24);
      border: 2px solid var(--color-button-primary);
      color: var( --color-interaction-accent-3);
      box-shadow: inset 0 0 0 2px #fff;
      transition: all 0.3s ease 0s;
    }
    &:disabled {
      color: rgba(22, 22, 22, 0.24);
    }
  }

  &--tertiary_onBrand {
    background-color: transparent;
    color: var( --color-interaction-on-brand-accent-1);

    &:hover {
      background-color: rgba(254, 254, 254, 0.12);
      color: var( --color-interaction-on-brand-accent-1);
    }

    &:focus {
      background-color:  rgba(254, 254, 254, 0.12);
      color: var( --color-interaction-on-brand-accent-1);
      border: 2px solid var( --color-interaction-on-brand-accent-1);
      box-shadow: inset 0 0 0 2px rgba(22, 22, 22, 0.24);
    }

    &:active {
      background-color:rgba(254, 254, 254, 0.24);
      color: var( --color-interaction-on-brand-accent-1);
    }

    &:disabled {
      color: rgba(254, 254, 254, 0.32);
    }
  }
}

.button-component:disabled {
  pointer-events: none;
}

.size--sm {
  min-width: 124px;
  height: 38px;
  width: 100%;
  font-size: var(--font-size-2);
}

.size--lg {
  min-width: 264px;
  height: 48px;
  width: 100%;
  font-size: var(--font-size-3);
}

</style>
